import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X } from "lucide-react";
import { PRACTICE_AREAS } from "../../data/practiceAreas";

const NAV_LINKS = [
  { label: "How It Works", to: "/#how-it-works" },
  { label: "FAQ", to: "/#faq" },
  { label: "Contact", to: "/contact" },
];

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileAreasOpen, setMobileAreasOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
    setDropdownOpen(false);
  }, [location]);

  useEffect(() => {
    function handleClick(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <header
      className="sticky top-0 z-50 bg-white border-b border-hairline"
      data-testid="main-nav"
    >
      <div className="max-w-content mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link
            to="/"
            className="text-xl font-extrabold text-navy tracking-tight"
            data-testid="nav-logo"
          >
            LegalConnect<span className="text-gold">360</span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-7">
            <div
              className="relative"
              ref={dropdownRef}
              onMouseEnter={() => setDropdownOpen(true)}
              onMouseLeave={() => setDropdownOpen(false)}
            >
              <button
                className="flex items-center gap-1 text-[15px] font-medium text-ink hover:text-gold transition-colors"
                onClick={() => setDropdownOpen((o) => !o)}
                data-testid="nav-practice-dropdown-trigger"
              >
                Legal Lead Generation
                <ChevronDown className="w-4 h-4" />
              </button>
              {dropdownOpen && (
                <div
                  className="absolute left-0 top-full w-72 bg-white border border-hairline shadow-sm rounded-[4px] py-1"
                  data-testid="nav-practice-dropdown"
                >
                  {PRACTICE_AREAS.map((area) => (
                    <Link
                      key={area.slug}
                      to={`/${area.slug}`}
                      className="block px-4 py-2 text-[14px] text-ink hover:bg-altbg hover:text-gold transition-colors"
                      data-testid={`nav-dropdown-${area.slug}`}
                    >
                      {area.navName}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {NAV_LINKS.map((l) => (
              <NavItem key={l.label} to={l.to} label={l.label} />
            ))}

            <Link
              to="/contact"
              className="bg-gold text-navy font-semibold text-[15px] px-5 py-2 rounded-[4px] hover:bg-navy hover:text-white transition-colors"
              data-testid="nav-get-started-btn"
            >
              Get Started
            </Link>
          </nav>

          {/* Mobile toggle */}
          <button
            className="lg:hidden text-navy p-2"
            onClick={() => setMobileOpen((o) => !o)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div
          className="lg:hidden border-t border-hairline bg-white"
          data-testid="mobile-menu"
        >
          <div className="px-4 py-3 space-y-1">
            <button
              className="w-full flex items-center justify-between py-2 text-[15px] font-medium text-ink"
              onClick={() => setMobileAreasOpen((o) => !o)}
              data-testid="mobile-practice-toggle"
            >
              Legal Lead Generation
              <ChevronDown
                className={`w-4 h-4 transition-transform ${
                  mobileAreasOpen ? "rotate-180" : ""
                }`}
              />
            </button>
            {mobileAreasOpen && (
              <div className="pl-4 border-l border-hairline space-y-1 mb-2">
                {PRACTICE_AREAS.map((area) => (
                  <Link
                    key={area.slug}
                    to={`/${area.slug}`}
                    className="block py-2 text-[14px] text-subink hover:text-gold"
                    data-testid={`mobile-dropdown-${area.slug}`}
                  >
                    {area.navName}
                  </Link>
                ))}
              </div>
            )}
            {NAV_LINKS.map((l) => (
              <Link
                key={l.label}
                to={l.to}
                className="block py-2 text-[15px] font-medium text-ink hover:text-gold"
                data-testid={`mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {l.label}
              </Link>
            ))}
            <Link
              to="/contact"
              className="block text-center bg-gold text-navy font-semibold px-5 py-2.5 rounded-[4px] mt-2"
              data-testid="mobile-get-started-btn"
            >
              Get Started
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

function NavItem({ to, label }) {
  const isHash = to.includes("#");
  const className =
    "text-[15px] font-medium text-ink hover:text-gold transition-colors";
  if (isHash) {
    return (
      <a href={to} className={className} data-testid={`nav-link-${label.toLowerCase().replace(/\s+/g, "-")}`}>
        {label}
      </a>
    );
  }
  return (
    <Link to={to} className={className} data-testid={`nav-link-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      {label}
    </Link>
  );
}
